import React, { useEffect, useRef, useState } from 'react';
import Typography from '../../components/Typography';
import axios from 'axios';
import admin_avatar from '../../images/admin_avatar.png';
import { io } from 'socket.io-client';
import { CustomSupportMessageList } from '../../components/CustomMessageList';
import CustomModal from '../../components/CustomModal';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../../components/CustomButton';

// Initialize Socket.IO client with authentication
const socket = io(`${process.env.REACT_APP_BACKEND_URL}`, {
  reconnection: true,
  reconnectionAttempts: 5,
  auth: {
    token: localStorage.getItem('token'),
  },
});

const Tickets = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [statusFilter, setStatusFilter] = useState('All');
  const [users, setUsers] = useState([]);
  const [activeId, setActiveId] = useState(id ?? null);
  const [openId, setOpenId] = useState(id ?? null);
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ticketsPerPage = 10;
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const chatContainerRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('success');
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalCloseText, setModalCloseText] = useState('OK');
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showResolvedModal, setShowResolvedModal] = useState(false);

  // Fetch tickets and users
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [userResponse, ticketResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/admin`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ticket/admin`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setUsers(userResponse.data.users || []);
        setTickets(ticketResponse.data.tickets || []);
        const activeIndex = activeId ? ticketResponse.data.tickets.findIndex((t) => t._id === activeId) : 0;
        const page = Math.floor(activeIndex / ticketsPerPage) + 1; // Calculate current page
        setCurrentPage(page);

        if (!activeId) {
          setActiveId(ticketResponse.data.tickets[0]._id);
        }
        setTicket(ticketResponse.data.tickets[activeIndex]);
        fetchTicketMessages(ticketResponse.data.tickets[activeIndex]._id);
        setShowResolvedModal(ticketResponse.data.tickets[activeIndex].status === 'Completed');
      } catch (error) {
        console.error('Error fetching users and tickets:', error);
        setError('Failed to load tickets. Please try again.');
      }
    };
    fetchData();
  }, [activeId,]);

  // Fetch messages for the selected ticket
  const fetchTicketMessages = async (ticketId) => {
    setError(null);
    setIsLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ticket/${ticketId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTicket((prev) => ({
        ...prev,
        messages: response.data.messages || [],
      }));
      setTickets((prevTickets) =>
        prevTickets.map((t) =>
          t._id === ticketId ? { ...t, messages: response.data.messages || [] } : t
        )
      );
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages. Please try again.');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Socket.IO: Join/leave ticket room and handle messages
  useEffect(() => {
    if (ticket?._id) {
      socket.emit('join_ticket', { ticketId: ticket._id });

      socket.on('receive_message', (message) => {
        if (message.ticketId === ticket._id) {
          setTicket((prevTicket) => ({
            ...prevTicket,
            messages: [...(prevTicket.messages || []), message],
          }));
          setTickets((prevTickets) =>
            prevTickets.map((t) =>
              t._id === ticket._id
                ? { ...t, messages: [...(t.messages || []), message] }
                : t
            )
          );
        }
      });

      socket.on('error', (data) => {
        setError(data.message);
      });
    }

    return () => {
      if (ticket?._id) {
        socket.emit('leave_ticket', { ticketId: ticket._id });
      }
      socket.off('receive_message');
      socket.off('error');
    };
  }, [ticket?._id]);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [ticket?.messages, isLoadingMessages]);

  const formatDate = (dateString) => {
    if (!dateString) return '[Insert Date]';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    });
  };

  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Invalid Time';
    }
  };

  // Filter tickets based on status
  const filteredTickets = tickets.filter((ticket) => {
    if (statusFilter === 'All') return true;
    return ticket.status === statusFilter;
  });

  // Calculate total pages
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);

  // Ensure currentPage stays within valid bounds
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
    if (currentPage < 1 && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredTickets, currentPage, totalPages]);

  // Calculate the tickets to display on the current page
  const startIndex = (currentPage - 1) * ticketsPerPage;
  const currentTickets = filteredTickets.slice(startIndex, startIndex + ticketsPerPage);

  // Handle page navigation
  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      // setActiveIndex(0);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      // setActiveIndex(0);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    // setActiveIndex(0);
  };

  // Generate page numbers (show up to 5 pages, adjust based on currentPage)
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !ticket?._id) return;

    const messageData = {
      ticketId: ticket._id,
      content: newMessage,
      sender: 'admin',
      timestamp: new Date().toISOString(),
    };

    try {
      socket.emit('send_message', messageData);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    }
  };

  const handleResolveTicket = async () => {
    if (!ticket?._id) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/ticket/${ticket._id}/resolve`,
        { status: 'Completed' },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      ).then((response) => {
        if (response.data.success) {
          setTickets((prevTickets) =>
            prevTickets.map((t) =>
              t._id === ticket._id ? { ...t, status: 'Completed' } : t
            )
          );
          setTicket((prev) => ({ ...prev, status: 'Completed' }));
        }
      }).finally(() => {
        setShowLoadingModal(false);
        setShowModal(true);
        setModalTitle('ALL DONE!');
        setModalMessage('Issue successfully resolved. You may now close this screen.');
        setModalType('success');
        setModalCloseText('RETURn TO DASHBOARD');
      });

    } catch (error) {
      console.error('Error resolving ticket:', error);
      setError('Failed to resolve ticket. Please try again.');
    }
  };

  const handleConfirm = async (title) => {
    if (title === 'issue resolved?') {
      setShowModal(false);
      setShowLoadingModal(true);
      setTimeout(() => {
        handleResolveTicket();
      }, 2000);
    }
    if (title === 'ALL DONE!') {
      setShowModal(false);
      navigate('/admin');
    }
  }

  return (
    <div className='lg:grid lg:grid-cols-2 flex flex-col-reverse gap-[14px]'>
      <div className="lg:col-span-1 p-5 rounded-[4px] bg-black">
        {showModal && (
          <CustomModal
            type={modalType}
            title={modalTitle}
            message={modalMessage}
            colseText={modalCloseText}
            onConfirm={() => handleConfirm(modalTitle)}
            onClose={() => setShowModal(false)}
          />
        )}
        {showLoadingModal && (
          <CustomModal
            type="loading"
            title="loading"
            message="Please wait while we mark the issue as resolved..."
            onClose={() => setShowLoadingModal(false)}
            colseText="Okay"
            confirmButton={false}
          />
        )}
        <div className="flex justify-between sm:flex-row flex-col w-full gap-4">
          <Typography
            text={'USER Tickets'}
            transform={'uppercase'}
            color={'#FFF'}
            size={'16px'}
            weight={400}
            lineheight={'170%'}
            family={'Supply'}
          />
          <div className="flex items-center gap-1">
            <button
              onClick={() => setStatusFilter('All')}
              className={`sm:inline-flex flex flex-1 h-7 px-[14px] py-[10px] justify-center items-center border-[1px] border-[#FFF] rounded-sm ${statusFilter === 'All' ? 'bg-[#DF0F11] border-none' : ''}`}
            >
              <Typography text={'All'} size={'12px'} color={'#FFF'} />
            </button>
            <button
              onClick={() => setStatusFilter('Pending')}
              className={`sm:inline-flex flex flex-1 h-7 px-[14px] py-[10px] justify-center items-center border-[1px] border-[#FFF] rounded-sm ${statusFilter === 'Pending' ? 'bg-[#DF0F11] border-none' : ''}`}
            >
              <Typography text={'Pending'} size={'12px'} color={'#FFF'} />
            </button>
            <button
              onClick={() => setStatusFilter('Completed')}
              className={`sm:inline-flex flex flex-1 h-7 px-[14px] py-[10px] justify-center items-center border-[1px] border-[#FFF] rounded-sm ${statusFilter === 'Completed' ? 'bg-[#DF0F11] border-none' : ''}`}
            >
              <Typography text={'Completed'} size={'12px'} color={'#FFF'} />
            </button>
          </div>
        </div>
        <div className='md:hidden block'>
          {currentTickets.map((ticket, index) => {
            const user = users.find((u) => u._id === ticket.userId);

            return (
              <div
                key={index}
                className="border-b flex-col flex items-start border-gray-700 py-5 cursor-pointer"
                onClick={() => {setActiveId(ticket._id);setOpenId(openId === ticket._id ? null : ticket._id);}}
              >
                <div className="flex flex-col  items-start self-stretch">
                  <div className="flex justify-between items-center self-stretch">
                    <div className='flex items-center gap-[6px]'>
                      <img
                        className={`${ticket.status === 'Completed' ? 'opacity-20' : ''} w-[26px] h-[26px] flex-shrink-0 rounded-[26px]`}
                        src={admin_avatar}
                        alt="admin_avatar"
                      />
                      <Typography text={user?.name || 'Unknown'} size={'14px'} weight={400} color={'#FFF'} />
                    </div>
                    {openId === ticket._id ? <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <mask id="mask0_4672_17113" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                        <rect width="14" height="14" transform="matrix(-4.37114e-08 1 1 4.37114e-08 0 3.05176e-05)" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_4672_17113)">
                        <path d="M12.6318 4.67558L11.804 3.84769L7.00049 8.65115L2.19704 3.84769L1.36914 4.67558L7.00049 10.3069L12.6318 4.67558Z" fill="#DF0F11" />
                      </g>
                    </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <mask id="mask0_4672_17226" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                        <rect width="14" height="14" transform="matrix(1.19249e-08 -1 -1 -1.19249e-08 14 14.0001)" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_4672_17226)">
                        <path d="M1.36815 9.32454L2.19605 10.1524L6.99951 5.34898L11.803 10.1524L12.6309 9.32454L6.99951 3.69318L1.36815 9.32454Z" fill="#DF0F11" />
                      </g>
                    </svg>}
                  </div>
                </div>

                {/* Optional expanded content */}
                {openId === ticket._id && (
                  <div className="flex flex-col mt-4 gap-4 justify-center items-start self-stretch animation transition-all animate-backdropFadeIn">
                    <div className="flex justify-between items-start self-stretch">
                      <Typography text={'Ticket No.'} opacity={0.6} size={'12px'} color='#FFF' />
                      <Typography text={ticket.ticketNo} size={'14px'} color='#FFF' />
                    </div>
                    <div className="flex justify-between items-start self-stretch">
                      <Typography text={'Query'} opacity={0.6} size={'12px'} color='#FFF' />
                      <Typography text={ticket.query} size={'14px'} color='#FFF' />
                    </div>
                    <div className="flex justify-between items-start self-stretch">
                      <Typography text={'Created on'} opacity={0.6} size={'12px'} color='#FFF' />
                      <Typography text={formatDate(ticket.createdOn)} size={'14px'} color='#FFF' />
                    </div>
                  </div>
                )}
              </div>)
          }
          )}
        </div>
        <div className="relative md:block hidden overflow-x-auto mt-3">
          <table className="w-full text-sm text-left rtl:text-right table-fixed">
            <thead className="text-xs text-white opacity-60">
              <tr className="border-b border-[#151515]">
                <th scope="col" className="px-6 w-[25%] py-3">
                  <Typography text={'User'} size={'14px'} weight={400} />
                </th>
                <th scope="col" className="w-[15%] py-3">
                  <Typography text={'Ticket No.'} size={'14px'} weight={400} />
                </th>
                <th scope="col" className="w-[45%] py-3">
                  <Typography text={'Query'} size={'14px'} weight={400} />
                </th>
                <th scope="col" className="w-[15%] py-3">
                  <Typography text={'Created on'} size={'14px'} weight={400} />
                </th>
              </tr>
            </thead>
            <tbody>
              {currentTickets.map((ticket, index) => {
                const user = users.find((u) => u._id === ticket.userId);
                return (
                  <tr
                    key={index}
                    onClick={() => setActiveId(ticket._id)}
                    className={`border-b py-4 border-[#151515] h-[50px] ${ticket.status === 'Completed' ? 'text-[#282828]' : 'text-white'} ${ticket._id === activeId ? 'bg-[#DF0F11] rounded' : ''}`}
                  >
                    <td className="w-[25%] px-6">
                      <div className='flex items-center gap-[6px]'>
                        <img
                          className={`${ticket.status === 'Completed' ? 'opacity-20' : ''} w-[26px] h-[26px] flex-shrink-0 rounded-[26px]`}
                          src={admin_avatar}
                          alt="admin_avatar"
                        />
                        <Typography text={user?.name || 'Unknown'} size={'14px'} weight={400} />
                      </div>
                    </td>
                    <td className="w-[15%]">
                      <Typography text={ticket.ticketNo} size={'14px'} weight={400} />
                    </td>
                    <td className="w-[45%]">
                      <Typography text={ticket.query} size={'14px'} weight={400} />
                    </td>
                    <td className="w-[15%]">
                      <Typography text={formatDate(ticket.createdOn)} size={'14px'} weight={400} />
                    </td>
                  </tr>
                );
              })}
            </tbody>

          </table>
          <div className="mt-4">
            <div className="flex justify-end items-center gap-[6px]">
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className={`flex flex-shrink-0 items-center justify-center w-[25px] h-[25px] rounded ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'opacity-100 cursor-pointer'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <mask id="mask0_498_376" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="11" height="11">
                    <rect width="10" height="10" transform="matrix(-1 0 0 1 10.5 0.5)" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_498_376)">
                    <path d="M7.15885 9.66659L7.89844 8.927L4.47135 5.49992L7.89844 2.07284L7.15885 1.33325L2.99219 5.49992L7.15885 9.66659Z" fill="white" />
                  </g>
                </svg>
              </button>
              {pageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => handlePageClick(pageNumber)}
                  className={`inline-flex flex-shrink-0 items-center justify-center h-[25px] p-[10px] rounded ${currentPage === pageNumber ? 'border border-white' : 'opacity-40'}`}
                >
                  <Typography
                    text={pageNumber.toString()}
                    size={'14px'}
                    weight={400}
                    lineheight={'170%'}
                    color={'#FFF'}
                  />
                </button>
              ))}
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`flex flex-shrink-0 items-center justify-center w-[25px] h-[25px] rounded ${currentPage === totalPages || totalPages === 0 ? 'opacity-40 cursor-not-allowed' : 'opacity-100 cursor-pointer'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <mask id="mask0_498_372" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="11" height="11">
                    <rect x="0.5" y="0.5" width="10" height="10" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_498_372)">
                    <path d="M3.84115 9.66659L3.10156 8.927L6.52865 5.49992L3.10156 2.07284L3.84115 1.33325L8.00781 5.49992L3.84115 9.66659Z" fill="white" />
                  </g>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="col-span-1 bg-black relative flex justify-center items-center">
        {showResolvedModal && <div className="absolute fade inset-0 z-50 flex items-center h-full justify-center bg-black bg-opacity-5 animate-backdropFadeIn">
          <div className="relative mx-auto animate-modalSlideIn">
            <svg xmlns="http://www.w3.org/2000/svg" className='sm:block hidden' width="435" height="266" viewBox="0 0 435 266" fill="none">
              <path d="M0 35.208L38.4985 0H435V265.87H0V35.208Z" fill="#1B1B1B" />
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" className='sm:hidden block' width="331" height="342" viewBox="0 0 331 342" fill="none">
              <path d="M0 53.5L58.5 0H330.5V342H0V53.5Z" fill="#1B1B1B" />
            </svg>
            <div className="absolute inset-0 flex flex-col py-[28px] px-[23px] items-center justify-between text-white text-center">
              {/* Close Icon */}
              <button
                className="absolute top-4 right-4 text-white text-lg hover:text-gray-300"
                aria-label="Close modal"
                onClick={() => setShowResolvedModal(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                  <g opacity="0.6">
                    <mask id="mask0_1769_5697" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="30" height="30">
                      <rect width="30" height="30" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_1769_5697)">
                      <path d="M8.00078 23.317L6.68359 21.9998L13.6836 14.9998L6.68359 7.99981L8.00078 6.68262L15.0008 13.6826L22.0008 6.68262L23.318 7.99981L16.318 14.9998L23.318 21.9998L22.0008 23.317L15.0008 16.317L8.00078 23.317Z" fill="white" />
                    </g>
                  </g>
                </svg>
              </button>
              <div className="">
                <svg xmlns="http://www.w3.org/2000/svg" width="58" height="57" viewBox="0 0 58 57" fill="none">
                  <mask id="mask0_1769_4625" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="58" height="57">
                    <rect x="0.875" y="0.297852" width="56.5961" height="56.5961" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_1769_4625)">
                    <path d="M29.1727 40.3869C29.8409 40.3869 30.401 40.1609 30.8529 39.709C31.3049 39.257 31.5309 38.6969 31.5309 38.0288C31.5309 37.3606 31.3049 36.8005 30.8529 36.3486C30.401 35.8966 29.8409 35.6706 29.1727 35.6706C28.5046 35.6706 27.9445 35.8966 27.4925 36.3486C27.0406 36.8005 26.8146 37.3606 26.8146 38.0288C26.8146 38.6969 27.0406 39.257 27.4925 39.709C27.9445 40.1609 28.5046 40.3869 29.1727 40.3869ZM26.8146 30.9543H31.5309V16.8052H26.8146V30.9543ZM20.3296 49.8196L7.94922 37.4392V19.7529L20.3296 7.37256H38.0159L50.3963 19.7529V37.4392L38.0159 49.8196H20.3296Z" fill="#DF0F11" />
                  </g>
                </svg>
              </div>
              <Typography text={'issue resolved!'} transform={'uppercase'} family={'Supply'} size={'23.691px'} />
              <Typography text={'This issue has been resolved. To view the conversation, click "Read Chat".'} className='w-56' opacity={0.6} color={'#FFF'} size={'11.846px'} />
              <CustomButton onClick={() => setShowResolvedModal(false)} className='h-[31.589px]' fontSize='11px' text={'Read Chat'} />
            </div>
          </div>
        </div>}
        <div className={`${showResolvedModal && 'backdrop-blur-3xl opacity-10'} bg-black w-full rounded relative md:p-6 p-4`} >
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <Typography
                text={`Ticket No. ${ticket?.ticketNo || 'N/A'}`}
                transform="uppercase"
                color="#FFF"
                size="16px"
                weight={400}
                family="Supply"
              />
              {ticket?.status !== 'Completed' && (
                <Typography
                  className="cursor-pointer"
                  text={'ISSUE RESOLVED?'}
                  size={'12.052px'}
                  textDecorationLine={'underline'}
                  textDecorationStyle={'solid'}
                  textDecorationSkipInk={'none'}
                  textDecorationThickness={'auto'}
                  textUnderlineOffset={'auto'}
                  textUnderlinePosition={'from-font'}
                  lineheight={'170%'}
                  family={'Supply'}
                  weight={400}
                  transform={'uppercase'}
                  color={'#DF0F11'}
                  onClick={() => {
                    setShowModal(true);
                    setModalType('confirm');
                    setModalMessage('Have you resolved the client’s issue? If so, click "Issue Resolved".');
                    setModalTitle('issue resolved?');
                    setModalCloseText('Issue Resolved');
                  }}
                />
              )}
            </div>
            <div className='mt-[18px] bg-white opacity-10 h-[1px]'></div>
          </div>

          <div
            className="chat-container bg-transparent rounded-lg md:p-6 flex flex-col h-[500px]"
          >
            {error && (
              <div className="text-red-500 text-sm mb-4">
                {error}
              </div>
            )}
            <div ref={chatContainerRef} className="flex-1 scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto">
              {isLoadingMessages ? (
                <div className="text-white text-center">Loading messages...</div>
              ) : ticket?.messages && ticket.messages.length > 0 ? (
                ticket.messages.map((message, index) => { 
                  const user = users.find((u) => u._id === ticket.userId);
                  return (
                  <div
                    key={index}
                    className={`mb-4 flex ${message.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <CustomSupportMessageList
                      message={message.content}
                      client_avatar={<img src={process.env.REACT_APP_BACKEND_URL+user?.avatar} className="w-8 h-8" alt="Client Avatar" />}
                      isSupport={message.sender === 'admin'}
                      timestamp={formatTime(message.timestamp)}
                    />
                  </div>
                )})
              ) : (
                <div className="text-white text-center opacity-50">
                  No messages yet. Start the conversation!
                </div>
              )}
            </div>

            {ticket?.status !== 'Completed' && (
              <>
                <form onSubmit={handleSendMessage} className="mt-4 flex items-center">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={'Ask us anything'}
                    style={{ fontFamily: 'Aeonik' }}
                    className="h-[40px] outline-none border-none w-full border-b border-[1px] border-[#4C4C4C] text-white bg-transparent text-[14px]"
                    required
                  />
                  <button
                    type="submit"
                    className="relative mb-[7px] flex items-center justify-center w-8 h-8 transition-all duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className='absolute inset-0 w-full h-full' width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <path d="M0 2C0 0.895431 0.895431 0 2 0H23.2485C23.733 0 24.201 0.175836 24.5655 0.494847L31.317 6.40238C31.751 6.78216 32 7.33081 32 7.90754V30C32 31.1046 31.1046 32 30 32H2C0.895431 32 0 31.1046 0 30V2Z" fill="#DF0F11" />
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" className='relative flex items-center justify-center z-10 space-x-2' width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <mask id="mask0_498_633" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                        <rect width="20" height="20" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_498_633)">
                        <path d="M2.91699 16.0416V3.95825L17.2599 9.99992L2.91699 16.0416ZM4.16699 14.1666L14.042 9.99992L4.16699 5.83325V8.91013L8.68616 9.99992L4.16699 11.0897V14.1666Z" fill="white" />
                      </g>
                    </svg>
                  </button>
                </form>
                <hr className='border-[#4C4C4C]' />
                <div className="flex flex-wrap gap-2 mt-3">
                  <div onClick={() => setNewMessage('Hurray! Your issue has been resolved')} className={`inline-flex md:w-auto w-full justify-center cursor-pointer items-center gap-[10px] px-[14px] py-[10px] border border-white ${newMessage !== 'Hurray! Your issue has been resolved' && 'border-opacity-30'}`}>
                    <Typography opacity={newMessage !== 'Hurray! Your issue has been resolved' && .4} color="#FFF" size="14px" weight={400} lineheight={'170%'} text="Hurray! Your issue has been resolved" />
                  </div>
                  <div onClick={() => setNewMessage('We will get back to you')} className={`inline-flex md:w-auto w-full justify-center cursor-pointer items-center gap-[10px] px-[14px] py-[10px] border border-white ${newMessage !== 'We will get back to you' && 'border-opacity-30'}`}>
                    <Typography opacity={newMessage !== 'We will get back to you' && .4} color="#FFF" size="14px" weight={400} lineheight={'170%'} text="We will get back to you" />
                  </div>
                  <div onClick={() => setNewMessage('Congrats!')} className={`inline-flex md:w-auto w-full justify-center cursor-pointer items-center gap-[10px] px-[14px] py-[10px] border border-white ${newMessage !== 'Congrats!' && 'border-opacity-30'}`}>
                    <Typography opacity={newMessage !== 'Congrats!' && .4} color="#FFF" size="14px" weight={400} lineheight={'170%'} text="Congrats!" />
                  </div>
                  <div onClick={() => setNewMessage('Please wait we will notify you soon')} className={`inline-flex md:w-auto w-full justify-center cursor-pointer items-center gap-[10px] px-[14px] py-[10px] border border-white ${newMessage !== 'Please wait we will notify you soon' && 'border-opacity-30'}`}>
                    <Typography opacity={newMessage !== 'Please wait we will notify you soon' && .4} color="#FFF" size="14px" weight={400} lineheight={'170%'} text="Please wait we will notify you soon" />
                  </div>
                  <div onClick={() => setNewMessage('Thank you!')} className={`inline-flex md:w-auto w-full justify-center cursor-pointer items-center gap-[10px] px-[14px] py-[10px] border border-white ${newMessage !== 'Thank you!' && 'border-opacity-30'}`}>
                    <Typography opacity={newMessage !== 'Thank you!' && .4} color="#FFF" size="14px" weight={400} lineheight={'170%'} text="Thank you!" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tickets;